import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../components/common/Input";
import Checkbox from "../../../components/common/Checkbox";
import Badge from "../../../components/common/Badge";
import {
  platformOptions,
  channelDisplayConfig,
} from "../constants/notificationConstants";
import {
  UserGroupIcon,
  DeviceMobileIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { fetchChannelStatusThunk } from "../../../store/thunks/campaignThunks";

/**
 * @param {object} props
 * @param {object} props.data           current draft slice
 * @param {object} props.segmentStats   counts keyed by segment
 * @param {function} props.onChange     updater
 */
const CampaignAudienceStep = ({ data, segmentStats = {}, onChange }) => {
  const dispatch = useDispatch();
  const [segmentInput, setSegmentInput] = useState("");
  const [deviceInput, setDeviceInput] = useState("");
  const { status: channelStatus, loading: channelsLoading } = useSelector(
    (state) => state.notificationCampaigns.channels
  );

  const predefinedSegments = [
    "all_users",
    "active_users",
    "new_users",
    "b2b_users",
    "b2c_users",
    "verified_users",
  ];

  // Fetch channel status when component mounts
  useEffect(() => {
    dispatch(fetchChannelStatusThunk());
  }, [dispatch]);

  // Get the enabled channels for filtering
  const getEnabledChannels = () => {
    return Object.entries(channelStatus)
      .filter(([_, enabled]) => enabled)
      .map(([channel]) => channel);
  };

  // Check if we only have one channel enabled
  const enabledChannels = useMemo(() => getEnabledChannels(), [channelStatus]);
  const hasOnlyOneChannel = enabledChannels.length === 1;

  // Auto-select the single channel if config says to
  useEffect(() => {
    if (
      channelDisplayConfig.autoSelectSingleChannel &&
      hasOnlyOneChannel &&
      enabledChannels.length > 0
    ) {
      const onlyEnabledChannel = enabledChannels[0];
      if (!data.platforms.includes(onlyEnabledChannel)) {
        onChange({ ...data, platforms: [onlyEnabledChannel] });
      }
    }
  }, [channelStatus, data.platforms, enabledChannels, hasOnlyOneChannel]);

  /* --------- handlers --------- */
  const toggleSegment = (seg) => {
    const next = data.segments.includes(seg)
      ? data.segments.filter((s) => s !== seg)
      : [...data.segments, seg];
    onChange({ ...data, segments: next });
  };

  const addCustomSeg = () => {
    if (segmentInput.trim()) {
      onChange({ ...data, segments: [...data.segments, segmentInput.trim()] });
      setSegmentInput("");
    }
  };

  const togglePlatform = (plat) => {
    // If we have only one channel enabled and auto-select is on, don't allow toggling
    if (channelDisplayConfig.autoSelectSingleChannel && hasOnlyOneChannel) {
      return;
    }

    if (plat === "all") return onChange({ ...data, platforms: ["all"] });

    const list = [...data.platforms];
    if (list.includes("all")) list.splice(list.indexOf("all"), 1);

    const idx = list.indexOf(plat);
    idx > -1 ? list.splice(idx, 1) : list.push(plat);
    onChange({ ...data, platforms: list.length ? list : ["all"] });
  };

  const addTestDev = () => {
    if (deviceInput.trim()) {
      onChange({
        ...data,
        testDevices: [...data.testDevices, deviceInput.trim()],
      });
      setDeviceInput("");
    }
  };

  /* --------- estimated reach string --------- */
  const estReachText = useMemo(() => {
    if (data.testDevices.length)
      return `${data.testDevices.length} test device(s)`;

    if (!data.segments.length) return "Select segments or add test devices";

    // sum counts (may double-count overlap; good enough for tooltip)
    const total = data.segments.reduce(
      (sum, s) => sum + (segmentStats[s] || 0),
      0
    );
    return total ? `≈ ${total.toLocaleString()} users` : "Calculating…";
  }, [data.segments, data.testDevices, segmentStats]);

  // Map platform values to channel keys
  const platformToChannel = {
    all: "all",
    FCM: "FCM",
    APNS: "APNS",
    WEB: "WEB",
    WHATSAPP: "WHATSAPP",
  };

  // Filter platform options based on configuration
  const visiblePlatformOptions = useMemo(() => {
    // Start with all options
    let options = [...platformOptions];

    // If we're set to hide disabled channels
    if (!channelDisplayConfig.showDisabled) {
      // Filter out options for disabled channels
      options = options.filter((opt) => {
        const channelKey = platformToChannel[opt.value];
        return channelKey === "all" || channelStatus[channelKey];
      });
    }

    // If we're auto-selecting the single channel and there's only one
    if (channelDisplayConfig.autoSelectSingleChannel && hasOnlyOneChannel) {
      // Hide the "all" option
      options = options.filter((opt) => opt.value !== "all");
    }

    return options;
  }, [channelStatus, hasOnlyOneChannel]);

  /* --------- view --------- */
  return (
    <div className="space-y-6">
      {/* segments */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <UserGroupIcon className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Target Segments</h3>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {predefinedSegments.map((seg) => (
            <label key={seg} className="cursor-pointer">
              <input
                type="checkbox"
                checked={data.segments.includes(seg)}
                onChange={() => toggleSegment(seg)}
                className="sr-only"
              />
              <span
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  data.segments.includes(seg)
                    ? "bg-primary text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                {seg.replace(/_/g, " ")}
                {segmentStats[seg] !== undefined && (
                  <span className="ml-2 text-xs opacity-70">
                    ({segmentStats[seg].toLocaleString()})
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>

        {/* custom segment input */}
        <div className="flex gap-2 mb-6">
          <Input
            value={segmentInput}
            onChange={(e) => setSegmentInput(e.target.value)}
            placeholder="Add custom segment…"
            onKeyPress={(e) => e.key === "Enter" && addCustomSeg()}
          />
          <button
            onClick={addCustomSeg}
            className="px-4 py-2 bg-primary text-white rounded-md"
          >
            Add
          </button>
        </div>

        {/* show any custom segments as badges */}
        {data.segments.filter((s) => !predefinedSegments.includes(s)).length >
          0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {data.segments
              .filter((s) => !predefinedSegments.includes(s))
              .map((seg) => (
                <Badge
                  key={seg}
                  variant="info"
                  className="flex items-center gap-1"
                >
                  {seg}
                  <button
                    onClick={() => toggleSegment(seg)}
                    className="ml-1 text-xs"
                  >
                    ×
                  </button>
                </Badge>
              ))}
          </div>
        )}
      </div>

      {/* platforms */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <DeviceMobileIcon className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Platforms</h3>
          {!channelsLoading &&
            Object.values(channelStatus).some((status) => !status) && (
              <div className="relative inline-block ml-1 group">
                <InformationCircleIcon className="w-4 h-4 text-blue-500 cursor-help" />
                <div className="absolute z-10 hidden group-hover:block bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 w-64 -mt-1 ml-6">
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Some channels are disabled by your administrator.
                  </p>
                </div>
              </div>
            )}
        </div>
        <div className="flex flex-wrap gap-2">
          {visiblePlatformOptions.map((opt) => {
            // Check if this platform/channel is enabled
            const channelKey = platformToChannel[opt.value];
            const isEnabled =
              channelKey === "all" ? true : channelStatus[channelKey];

            // Skip if we're configured to hide disabled channels and this one is disabled
            if (!channelDisplayConfig.showDisabled && !isEnabled) {
              return null;
            }

            // For auto-selected single channel, always show as selected and disabled toggle
            const isForceSelected =
              channelDisplayConfig.autoSelectSingleChannel &&
              hasOnlyOneChannel &&
              channelKey !== "all" &&
              isEnabled;

            // Prevent disabled channels from appearing selected
            const isSelected =
              isEnabled &&
              (data.platforms.includes(opt.value) || isForceSelected);

            return (
              <div key={opt.value} className="relative">
                <div className={`${!isEnabled ? "opacity-75" : ""}`}>
                  <label
                    className={`flex items-center ${
                      isEnabled && !isForceSelected
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() =>
                        isEnabled &&
                        !isForceSelected &&
                        togglePlatform(opt.value)
                      }
                      className="sr-only"
                      disabled={!isEnabled || isForceSelected}
                    />
                    <span
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? "bg-primary text-white"
                          : isEnabled
                          ? "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-700"
                      }`}
                    >
                      {opt.label}
                      {!isEnabled && (
                        <span className="ml-1 text-xs">(Disabled)</span>
                      )}
                      {isForceSelected && (
                        <span className="ml-1 text-xs">(Only available)</span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        {!channelsLoading && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {Object.values(channelStatus).some((status) => !status)
              ? "Some notification channels are disabled by your administrator."
              : "All notification channels are enabled."}
          </p>
        )}
      </div>

      {/* test devices */}
      <div>
        <h3 className="font-semibold mb-3">Test Devices (optional)</h3>
        <div className="flex gap-2 mb-3">
          <Input
            value={deviceInput}
            onChange={(e) => setDeviceInput(e.target.value)}
            placeholder="Device ID…"
            onKeyPress={(e) => e.key === "Enter" && addTestDev()}
          />
          <button
            onClick={addTestDev}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md"
          >
            Add
          </button>
        </div>

        {data.testDevices.length > 0 && (
          <div className="space-y-1 mb-6">
            {data.testDevices.map((d) => (
              <div
                key={d}
                className="flex justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded"
              >
                <span className="font-mono text-sm">{d}</span>
                <button
                  onClick={() =>
                    onChange({
                      ...data,
                      testDevices: data.testDevices.filter((x) => x !== d),
                    })
                  }
                  className="text-red-500 text-xs"
                >
                  remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* estimated reach */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Estimated reach:</strong> {estReachText}
        </p>
      </div>
    </div>
  );
};

export default CampaignAudienceStep;
